import { useTranslation } from '@repo/i18n'
import { Check, Monitor, Moon, Palette, Sun } from 'lucide-react'
import { useTheme } from '#components/theme-provider'
import { Button } from '#ui/button'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '#ui/drawer'
import {
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger
} from '#ui/dropdown-menu'
import { useSidebar } from '#ui/sidebar'

const THEME_OPTIONS = [
	{ value: 'light', icon: Sun },
	{ value: 'dark', icon: Moon },
	{ value: 'system', icon: Monitor }
] as const

type ThemeValue = (typeof THEME_OPTIONS)[number]['value']

function ThemePicker() {
	const { t } = useTranslation()
	const { isMobile } = useSidebar()
	const { theme, setTheme } = useTheme()

	if (isMobile) {
		return (
			<Drawer showSwipeHandle>
				<DrawerTrigger
					render={
						<Button
							className='h-11 justify-start gap-3 px-3 font-normal'
							variant='ghost'
						/>
					}
				>
					<Palette />
					<div className='flex flex-1 flex-col text-left'>
						<span>{t('shared:app_sidebar.menu.theme')}</span>
						<span className='text-muted-foreground text-xs'>
							{t(`shared:app_sidebar.theme.${theme}`)}
						</span>
					</div>
				</DrawerTrigger>
				<DrawerContent>
					<DrawerHeader>
						<DrawerTitle>
							{t('shared:app_sidebar.menu.theme')}
						</DrawerTitle>
						<DrawerDescription className='sr-only'>
							{t('shared:app_sidebar.menu.theme')}
						</DrawerDescription>
					</DrawerHeader>
					<div className='flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2 pb-4'>
						{THEME_OPTIONS.map(({ value, icon: Icon }) => {
							const isSelected = theme === value
							return (
								<DrawerClose
									key={value}
									render={
										<Button
											className='h-11 justify-start gap-3 px-3 font-normal'
											onClick={() =>
												setTheme(value as ThemeValue)
											}
											variant={
												isSelected
													? 'secondary'
													: 'ghost'
											}
										/>
									}
								>
									<Icon />
									<span className='flex-1 text-left'>
										{t(`shared:app_sidebar.theme.${value}`)}
									</span>
									{isSelected ? (
										<Check className='size-4 text-muted-foreground' />
									) : null}
								</DrawerClose>
							)
						})}
					</div>
				</DrawerContent>
			</Drawer>
		)
	}

	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger>
				<Palette />
				<div>
					<span className='block'>
						{t('shared:app_sidebar.menu.theme')}
					</span>
					<span className='block truncate text-muted-foreground! text-xs'>
						{t(`shared:app_sidebar.theme.${theme}`)}
					</span>
				</div>
			</DropdownMenuSubTrigger>
			<DropdownMenuSubContent className='w-40'>
				<DropdownMenuRadioGroup
					onValueChange={(value) => setTheme(value as ThemeValue)}
					value={theme}
				>
					<DropdownMenuRadioItem
						className='data-checked:bg-accent data-checked:text-accent-foreground data-checked:**:text-accent-foreground'
						closeOnClick
						value='light'
					>
						<Sun />
						<span>{t('shared:app_sidebar.theme.light')}</span>
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem
						className='data-checked:bg-accent data-checked:text-accent-foreground data-checked:**:text-accent-foreground'
						closeOnClick
						value='dark'
					>
						<Moon />
						<span>{t('shared:app_sidebar.theme.dark')}</span>
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem
						className='data-checked:bg-accent data-checked:text-accent-foreground data-checked:**:text-accent-foreground'
						closeOnClick
						value='system'
					>
						<Monitor />
						<span>{t('shared:app_sidebar.theme.system')}</span>
					</DropdownMenuRadioItem>
				</DropdownMenuRadioGroup>
			</DropdownMenuSubContent>
		</DropdownMenuSub>
	)
}

export { ThemePicker }
